// Modifications (spec §8) with the documented magnitude from the research report's tables. Stacking is
// multiplicative in resolveBuild. Every claim is one honest sentence; estimates say so.
import type { ModSpec, CraftSpec } from '../types';
import { galToKg, lbToKg, ftToM, AVGAS_KG_PER_L, JETA_KG_PER_L } from '../physics/atmosphere';
import { CRAFT } from './craft';
import { SRC } from './sources';
import { SEA_MODS } from './modsSea';

const AIR_PISTONS = ['trainer-172', 'fighter-p51'];
const JETS = ['airliner-737', 'multirole-f16', 'twin-aisle-777'];

/** Weight-reduction mods are per craft because ModEffect carries an absolute mass, not a fraction. */
function weightReduction(c: CraftSpec): ModSpec {
  const empty = c.airframe ? c.airframe.emptyMassKg : 0;
  return {
    id: `weight-reduction-${c.id}`,
    name: 'Weight reduction programme',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: [c.id],
    costCredits: 400,
    effect: { emptyMassAddKg: -0.02 * empty },
    claim: `−2 % empty mass (${Math.round(0.02 * empty)} kg; estimate): +10 % gross weight costs about 9 % specific range on a propeller aircraft and 5 % on a jet (code7700)`,
    sources: [SRC.code7700],
  };
}

export const MODS: ModSpec[] = [
  // ------------------------------------------------------------------ aircraft: engine
  {
    id: 'engine-monitor',
    name: 'Engine monitor and balanced injectors',
    category: 'engine',
    domain: 'air',
    // No fuel-injected piston is in the v1 roster (the audio verification put the carbureted O-300-D in the trainer),
    // so this mod is installable nowhere yet; it stays for the day an injected engine joins.
    appliesTo: [],
    costCredits: 400,
    effect: { enablesLop: true },
    claim: 'Lets you lean 30–70 °F lean of peak EGT: about −15 % fuel per hp·h at −8 % power (Mooney 231 data: +41 % range at −6 % speed); Lycoming does not recommend it',
    sources: [SRC.avwebAvgas, SRC.lycomingSSP700, SRC.gamiAvweb, SRC.lycomingSI1094],
  },
  {
    id: 'electronic-ignition',
    name: 'Electronic ignition',
    category: 'engine',
    domain: 'air',
    appliesTo: AIR_PISTONS,
    costCredits: 300,
    effect: { bsfcMul: 0.94 },
    claim: '−6 % fuel per hp·h in the model; the vendor claims 10–15 %, the STC test aircraft went 11.2 → 9.0 gal/h, and Aviation Consumer warns of under 5 % on small engines',
    sources: [SRC.aopaFrugal, SRC.gaNewsElectroair, SRC.aviationConsumerIgnition],
  },
  {
    id: 'turbonormalizer',
    name: 'Turbonormalizer',
    category: 'engine',
    domain: 'air',
    appliesTo: [], // no documented kit exists for the carbureted O-300-D in the 172E; offered again when a craft with a real STC joins the roster
    costCredits: 1500,
    effect: { criticalAltM: ftToM(20000), emptyMassAddKg: lbToKg(28.2) },
    claim: 'Holds sea-level manifold pressure to 20,000 ft (Tornado Alley Bonanza data) for +28.2 lb per turbocharger; it buys true airspeed at the same fuel flow, not fuel per mile',
    sources: [SRC.aopaBonanza, SRC.wikiIo550, SRC.theShop],
  },
  // ------------------------------------------------------------------ aircraft: propeller
  {
    id: 'constant-speed-prop',
    name: 'Constant-speed propeller conversion',
    category: 'propulsor',
    domain: 'air',
    appliesTo: [], // no documented kit exists for the carbureted O-300-D in the 172E; offered again when a craft with a real STC joins the roster
    costCredits: 900,
    effect: { propeller: 'constant-speed', emptyMassAddKg: 11 },
    claim: 'Same efficiency as a matched fixed-pitch propeller at its design speed, 5–12 % better away from it (Kitplanes; vendor calculator); +11 kg for propeller and governor (estimate)',
    sources: [SRC.kitplanesProp, SRC.firgelliProp],
  },
  {
    id: 'three-blade-prop',
    name: 'Three-blade propeller',
    category: 'propulsor',
    domain: 'air',
    appliesTo: [], // no documented kit exists for the carbureted O-300-D in the 172E; offered again when a craft with a real STC joins the roster
    costCredits: 600,
    effect: { propEffMul: 0.97, emptyMassAddKg: 5 },
    claim: '−3 % propeller efficiency in cruise (Hartzell: two blades are more efficient; Aviation Consumer: "a couple of knots", Mooney 252 conversions lost 4–6 kt) — better climb and smoother, shown honestly as a range loss',
    sources: [SRC.hartzellFaq, SRC.aviationConsumerBlades],
  },
  // ------------------------------------------------------------------ aircraft: fuel and weight
  {
    id: 'aux-tank-172',
    name: 'Long-range wing tanks',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: ['trainer-172'],
    costCredits: 500,
    effect: { fuelCapacityAddKg: galToKg(13, AVGAS_KG_PER_L), emptyMassAddKg: 10 },
    claim: '+13 US gal: the optional 52 gal long-range tanks of the 1960s Skyhawks against the standard 39 gal (estimate for the usable share); +10 kg installed (estimate)',
    sources: [SRC.wikiC172, SRC.avwebAvgas],
  },
  {
    id: 'drop-tanks-75',
    name: 'Two 75 US gal drop tanks',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: ['fighter-p51'],
    costCredits: 400,
    effect: { fuelCapacityAddKg: galToKg(150, AVGAS_KG_PER_L), emptyMassAddKg: 50, cd0Add: 0.002, jettisonWhenEmpty: true },
    claim: '+150 US gal in two 75 gal tanks, dropped when empty; their drag (+0.002 CD0, estimate: no quantified P-51 figure exists) and 50 kg (estimate) go with them',
    sources: [SRC.wikiDropTank, SRC.wikiP51],
  },
  {
    id: 'drop-tanks-108',
    name: 'Two 108 US gal drop tanks',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: ['fighter-p51'],
    costCredits: 600,
    effect: { fuelCapacityAddKg: galToKg(216, AVGAS_KG_PER_L), emptyMassAddKg: 70, cd0Add: 0.0028, jettisonWhenEmpty: true },
    claim: '+216 US gal in two 108 gal tanks, dropped when empty; drag +0.0028 CD0 and 70 kg are estimates (Wikipedia: part of a tank\'s fuel goes into carrying it)',
    sources: [SRC.wikiDropTank, SRC.wikiP51],
  },
  {
    id: 'wing-tanks-370',
    name: 'Two 370 US gal wing tanks',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: ['multirole-f16'],
    costCredits: 500,
    effect: { fuelCapacityAddKg: galToKg(740, JETA_KG_PER_L), emptyMassAddKg: 280, cd0Add: 0.0025, jettisonWhenEmpty: true },
    claim: '+740 US gal in two 370 gal Sargent Fletcher tanks, jettisoned when empty; drag +0.0025 CD0 and 280 kg are estimates',
    sources: [SRC.wikiF16, SRC.wikiDropTank],
  },
  {
    id: 'centreline-tank-300',
    name: '300 US gal centreline tank',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: ['multirole-f16'],
    costCredits: 300,
    effect: { fuelCapacityAddKg: galToKg(300, JETA_KG_PER_L), emptyMassAddKg: 120, cd0Add: 0.0012, jettisonWhenEmpty: true },
    claim: '+300 US gal on the centreline station, jettisoned when empty; drag +0.0012 CD0 and 120 kg are estimates',
    sources: [SRC.wikiF16, SRC.wikiDropTank],
  },
  {
    id: 'conformal-tanks',
    name: 'Conformal fuel tanks',
    category: 'fuel-weight',
    domain: 'air',
    appliesTo: ['multirole-f16'],
    costCredits: 900,
    effect: { fuelCapacityAddKg: galToKg(450, JETA_KG_PER_L), emptyMassAddKg: 410, cd0Add: 0.0006 },
    claim: '+450 US gal (estimate) in shoulder-mounted conformal tanks that stay on: +410 kg (estimate) and far less drag than drop tanks (+0.0006 CD0, estimate)',
    sources: [SRC.wikiF16],
  },
  // ------------------------------------------------------------------ aircraft: airframe and operations
  {
    id: 'winglets',
    name: 'Blended winglets',
    category: 'hull-airframe',
    domain: 'air',
    appliesTo: ['airliner-737'],
    costCredits: 800,
    effect: { inducedMul: 0.96, emptyMassAddKg: 180 },
    claim: '−4 % induced drag (estimate within the 3–5 % block-fuel band quoted for 737 blended winglets on long sectors) for +180 kg (estimate)',
    sources: [SRC.wikiWingtip, SRC.wiki737ng],
  },
  {
    id: 'engine-wash',
    name: 'Engine core wash',
    category: 'operations',
    domain: 'air',
    appliesTo: JETS,
    costCredits: 150,
    effect: { tsfcMul: 0.99 },
    claim: '−1 % thrust-specific fuel consumption (estimate: the size of the CFM56-7BE and Trent XWB EP improvements; the wash benefit itself is not sourced)',
    sources: [SRC.wikiCfm56, SRC.wikiTrentXwb],
  },
  {
    id: 'leap-class-engine',
    name: 'Newer-generation engine (LEAP class)',
    category: 'engine',
    domain: 'air',
    appliesTo: ['airliner-737'],
    costCredits: 1500,
    effect: { tsfcMul: 0.84, emptyMassAddKg: 2 * lbToKg(6130 - 5216) },
    claim: '−16 % fuel per unit thrust (CFM\'s LEAP claim against the CFM56; cruise TSFC 0.53 vs about 0.59 installed) for +914 lb per engine',
    sources: [SRC.wikiLeap, SRC.wikiCfm56],
  },
  ...CRAFT.filter((c) => c.domain === 'air').map(weightReduction),
  ...SEA_MODS,
];

export function modById(id: string): ModSpec | undefined {
  return MODS.find((m) => m.id === id);
}

/** Mods installable on a craft, in roster order. */
export function modsFor(craftId: string): ModSpec[] {
  const craft = CRAFT.find((c) => c.id === craftId);
  if (!craft) return [];
  return MODS.filter((m) => m.domain === craft.domain && (m.appliesTo.includes('*') || m.appliesTo.includes(craftId)));
}
